import React from "react";
import { useApp } from "./store.jsx";
import { BackLink, SectionHead } from "./chrome.jsx";
import { DrawBanner } from "./reveal.jsx";
import { hrefFor } from "./router.jsx";

const { Button, IconButton, Icon, Badge, Card, FluteDivider, Tooltip, EmptyState, Skeleton } =
  window.CardboardmarketDesignSystem_2a3239;

/**
 * Inviting somebody, and what it earns.
 *
 * A REFERRAL IS A FACT, A CAMPAIGN IS A QUESTION ASKED OF IT. The link and the
 * code work whether or not a draw is running — that is why this screen exists
 * even when there is nothing to win — and a draw that starts next week counts
 * the friends who joined this week. Saying "no draw is running" and stopping
 * would be untrue and would cost us the invitations sent in between.
 */

/** The invite URL, absolute, because it is going into somebody else's chat. */
export function inviteUrl(code) {
  return window.location.origin + hrefFor({ name: 'invited', id: code });
}

/** What the URL looks like when it is shown rather than followed. */
const inviteLabel = (code) => inviteUrl(code).replace(/^https?:\/\//, '');

/**
 * Copying, with the clipboard treated as something that can refuse.
 *
 * It does refuse: `navigator.clipboard` is undefined over plain HTTP and
 * throws when the document is not focused. Telling somebody their link is
 * copied when it is not is worse than not offering the button, so the failure
 * says so and the code stays on screen to be read out instead.
 */
export function useCopy() {
  const { notify, t } = useApp();
  return React.useCallback((text, message) => {
    const ok = () => notify(message, 'info');
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(ok, () => notify(t("We could not copy it. You can select it and copy it yourself."), 'error'));
        return;
      }
    } catch (e) { /* falls through to the same message */ }
    notify(t("We could not copy it. You can select it and copy it yourself."), 'error');
  }, [notify, t]);
}

/**
 * What a friend has to do, in the campaign's own terms.
 *
 * The API returns the condition as a value (`listing`, `want`, …) rather than a
 * sentence, like every other endpoint — so the sentence is built here, once,
 * and every screen that describes a draw uses this one.
 */
export function requirementPhrase(campaign, t) {
  switch (campaign && campaign.requires) {
    case 'want': return t("post a want");
    case 'listing_or_want': return t("post a card or a want");
    case 'trade': return t("complete a trade");
    case 'signup': return t("join");
    default: return t("post a card");
  }
}

export function CampaignTeaser({ campaign, compact }) {
  const { go, t, locale } = useApp();
  const live = campaign.state === 'live';
  const ends = new Date(campaign.endsAt);
  const daysLeft = Math.max(0, Math.ceil((ends - Date.now()) / 86400000));
  const day = (iso) => new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  return (
    <Card padding="0" interactive onClick={() => go('campaign', { id: campaign.id })} style={{ overflow: 'hidden' }}>
      <FluteDivider tone={live ? 'brand' : 'default'} />
      <div style={{ padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          {live ? <Badge tone="brand">{t("Open")}</Badge>
                : <Badge tone="neutral">{t("Starts {day}", { day: day(campaign.startsAt) })}</Badge>}
          {live ? <span className="cbm-meta">{t("{n} days left", { n: daysLeft })}</span> : null}
        </div>
        <div style={{ font: 'var(--type-h3)', color: 'var(--text-strong)', textWrap: 'pretty' }}>{campaign.title}</div>
        {!compact ? (
          <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
            {t("One entry for every friend who joins and does this: {what}.", { what: requirementPhrase(campaign, t) })}
            {' '}
            {t("{n} winners, drawn {day}.", { n: campaign.winners, day: day(campaign.drawAt) })}
          </span>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ font: 'var(--type-price)', color: 'var(--text-strong)' }}>
            {campaign.myEntries == null ? '–' : campaign.myEntries}{' '}
            <span className="cbm-meta">{t("your entries")}</span>
          </span>
          <Icon name="chevron-right" size={18} />
        </div>
      </div>
    </Card>
  );
}

function Friend({ friend }) {
  const { t, locale } = useApp();
  const joined = new Date(friend.joinedAt).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  /*
   * TWO STATES, NOT THE DESIGN'S FOUR. "Invited" and "doesn't count" describe
   * things this product cannot produce: a referral row only exists once
   * somebody has actually signed up with the code, and only a new account can
   * ever be referred — that is a UNIQUE constraint, not a rule we check.
   *
   * AND IT READS THE ENTRIES, NOT WHETHER THEY POSTED.
   *
   * Reported: "Waiting" beside friends who had already been counted. The draw
   * asked for `signup` — joining was enough — while this badge asked "have
   * they posted a card", which is a different question. It agreed with the
   * draw only while every campaign happened to want a listing.
   *
   * An entry is the fact. It already respects the campaign's condition, its
   * window and its cap, and reading it means there is no second copy of the
   * rule here to drift from the one in `awardEntries`.
   */
  const earned = (friend.entries || 0) > 0;
  const badge = earned
    ? <Badge tone="verified" icon="check">{t("Counts")}</Badge>
    : <Badge tone="caution">{t("Waiting")}</Badge>;
  return (
    <div className="cbm-friend">
      <span className="cbm-friend__init">{(friend.name || '?').slice(0, 1).toUpperCase()}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {friend.name}
        </div>
        <div className="cbm-meta">{t("Joined {day}", { day: joined })}</div>
      </div>
      {earned ? badge : (
        /*
         * WHY they are waiting depends on the draw, and this screen does not
         * know which rule applies to which friend — so it says the true thing
         * both cases share rather than guessing at a condition.
         */
        <Tooltip content={t("They count once they have done what the draw asks. We do not tell them you are waiting.")}>
          {badge}
        </Tooltip>
      )}
    </div>
  );
}

export function InviteScreen() {
  const { referral, referralState, loadReferral, campaigns, campaignsState, loadCampaigns, liveCampaign, unrevealed, go, t, locale } = useApp();
  const copy = useCopy();

  React.useEffect(() => { void loadReferral(); }, [loadReferral]);
  React.useEffect(() => { void loadCampaigns(); }, [loadCampaigns]);

  const c = liveCampaign;
  const next = campaigns.filter((x) => x.state === 'scheduled')
    .slice().sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))[0];
  const past = campaigns.filter((x) => x.state === 'ended');


  const code = referral && referral.code;
  const url = code ? inviteUrl(code) : '';
  const share = () => {
    /*
     * The system sheet where there is one — it is how people actually send a
     * link on a phone — and copying where there is not. A rejected share is not
     * a failure worth reporting: dismissing the sheet rejects the promise.
     */
    try {
      if (navigator.share) {
        navigator.share({ title: 'Cardboardmarket', text: t("Trade cards with me on Cardboardmarket"), url }).catch(() => {});
        return;
      }
    } catch (e) { /* falls through to copying */ }
    copy(url, t("Link copied. Paste it wherever your friends are."));
  };

  if (referralState === 'error') {
    return (
      <>
        <BackLink />
        <EmptyState icon="alert-triangle" title={t("We could not load your invite link")}
                    action={<Button onClick={() => loadReferral()}>{t("Try again")}</Button>} />
      </>
    );
  }

  return (
    <>
      <BackLink />
      <SectionHead
        title={t("Invite friends")}
        sub={c
          ? t("Every friend who joins and does this is one entry in the draw: {what}.", { what: requirementPhrase(c, t) })
          : next
            /*
             * A THIRD PLACE THE SAME CLAIM WAS HIDING, found by scanning every
             * rendered string for one rather than by waiting to be shown
             * another screenshot. "Invite somebody now — they count from the
             * moment it opens" is exactly what the window rule refuses: a
             * friend who joins before `starts_at` earns nothing.
             */
            ? (next.lookbackDays
              ? t("The next draw opens {day}, and counts friends from the {n} days before that. Inviting now counts.",
                  { day: new Date(next.startsAt).toLocaleDateString(locale, { day: 'numeric', month: 'long' }), n: next.lookbackDays })
              : t("The next draw opens {day}. Friends who join once it is open count towards it.",
                  { day: new Date(next.startsAt).toLocaleDateString(locale, { day: 'numeric', month: 'long' }) }))
            /*
             * AND NOW IT IS TRUE AGAIN, which is what the lookback was for.
             *
             * This said invites "count once the next draw starts"; the
             * campaign-window rule made that false and the sentence had to go.
             * With a 30-day lookback a friend who joins today DOES count for a
             * draw that opens inside the month — so the sentence comes back,
             * with the bound in it rather than as an open promise.
             */
            : t("No draw is running. Invite somebody anyway — friends who joined in the last 30 days count for the next one.")}
      />
      {/* A result waiting, given the room the strip cannot. */}
      {unrevealed ? <div style={{ marginBottom: 'var(--space-7)' }}><DrawBanner campaign={unrevealed} /></div> : null}
      <div className="cbm-split cbm-split--aside">
        <div className="cbm-stack" style={{ gap: 'var(--space-8)' }}>
          <Card cut padding="var(--space-6)" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', boxShadow: 'var(--shadow-hard)' }}>
            {!code ? (
              /*
               * The shape it will take, not a spinner. This card is the tallest
               * thing on the screen and the page would otherwise jump once the
               * code arrives — measured against the rendered card, not guessed.
               */
              <div className="cbm-stack" style={{ gap: 'var(--space-5)' }}>
                <Skeleton height={16} width="40%" />
                <Skeleton height={44} />
                <Skeleton height={16} width="30%" />
                <Skeleton height={32} width="55%" />
              </div>
            ) : (
              <>
                <div className="cbm-stack" style={{ gap: 8 }}>
                  <span className="cbm-label">{t("Your link")}</span>
                  <div className="cbm-linkbox">
                    <code>{inviteLabel(code)}</code>
                    <Button size="sm" variant="secondary"
                            onClick={() => copy(url, t("Link copied. Paste it wherever your friends are."))}>
                      {t("Copy")}
                    </Button>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
                  <div className="cbm-stack" style={{ gap: 6, flex: 1, minWidth: 180 }}>
                    <span className="cbm-label">{t("Or say your code")}</span>
                    <span className="cbm-code-big">{code}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                    <Button iconLeft="share-2" onClick={share}>{t("Share link")}</Button>
                    <Button variant="outline" onClick={() => copy(code, t("Code copied."))}>{t("Copy code")}</Button>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/*
            NO "HOW IT COUNTS" HERE ANY MORE, at the owner's ask: "I would just
            remove the whole 3 steps and the How it counts section."

            It was trying to explain rules that belong to a campaign, from a
            screen that is about your link. With a draw running it duplicated
            the draw's own page; with none it described a campaign that did not
            exist — which is what he caught. The teaser beside this and the
            draw's page carry it, and both are about one specific draw.
          */}
          <section className="cbm-stack" style={{ gap: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--space-4)', gap: 8, flexWrap: 'wrap' }}>
              <h2 className="cbm-h2" style={{ fontSize: 'var(--text-lg)' }}>{t("Your friends")}</h2>
            </div>
            {referral && referral.friends.length === 0 ? (
              <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
                {t("Nobody yet. They appear here as soon as somebody joins with your code.")}
              </span>
            ) : null}
            {(referral ? referral.friends : []).map((f) => <Friend key={f.id} friend={f} />)}
          </section>
        </div>

        <aside className="cbm-stack" style={{ gap: 'var(--space-6)' }}>
          {/*
            THE DRAW THAT IS OPEN, AND THE ONE THAT IS COMING.

            The scheduled teaser used to render only when a live draw existed
            too, which had it exactly backwards: with nothing open, the next
            one is the most interesting thing on this screen, and the owner
            published a draw starting tomorrow and found no trace of it
            anywhere. So a scheduled draw is always shown, and the "nothing at
            all" card is kept for when there is genuinely nothing.
          */}
          {c ? <CampaignTeaser campaign={c} /> : null}
          {next ? <CampaignTeaser campaign={next} compact={Boolean(c)} /> : null}
          {!c && !next ? (
            <Card tone="sunken" padding="var(--space-5)" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: 'var(--type-ui)', fontSize: 'var(--text-base)', color: 'var(--text-strong)' }}>{t("No draw running")}</span>
              <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>
                {/*
                  THE SAME FALSE CLAIM, IN A SECOND PLACE. This said invites
                  "still count when the next one opens", which the
                  campaign-window rule made untrue — and he found it here after
                  I had corrected only the headline. One sentence can be wrong
                  in two files, and fixing the one that was screenshotted is
                  not the same as fixing the claim.
                */}
                {campaignsState === 'loading'
                  ? t("Checking…")
                  : t("A draw usually counts friends from the 30 days before it opened, so inviting now is not wasted.")}
              </span>
            </Card>
          ) : null}
          {past.length ? (
            <div className="cbm-stack" style={{ gap: 0 }}>
              <span className="cbm-label" style={{ marginBottom: 'var(--space-3)' }}>{t("Past draws")}</span>
              {past.map((p) => (
                <button key={p.id} type="button" className="cbm-pastrow" onClick={() => go('campaign', { id: p.id })}>
                  <div style={{ flex: 1 }}>
                    <div style={{ font: 'var(--type-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>{p.title}</div>
                    <div className="cbm-meta">
                      {/* A separate key for the singular, which is how this
                          product does plurals — "1 entries" was on screen. */}
                      {p.myEntries == null ? t("Ended")
                        : p.myEntries === 1 ? t("1 entry")
                        : t("{n} entries", { n: p.myEntries })}
                    </div>
                  </div>
                  {/*
                    THE RESULT, ONCE THEY HAVE LOOKED — and not a second before.

                    `youWon` is null for everybody but the winner, so this is
                    only ever your own row (the winners' names are on the
                    campaign page, once each has watched).
                    But it is gated on `revealed` as well, because a badge here
                    would tell somebody how their draw went before they pressed
                    the button that exists to tell them.
                  */}
                  {!p.revealed ? null
                    : p.youWon ? <Badge tone="verified" icon="star">{t("You won")}</Badge>
                    : (p.myEntries || 0) > 0 ? <Badge tone="neutral">{t("Not drawn")}</Badge>
                    : null}
                  <Icon name="chevron-right" size={16} />
                </button>
              ))}
            </div>
          ) : null}
        </aside>
      </div>
    </>
  );
}
